import React, { useEffect, useRef, useState } from "react";

import { useGetObjectDataset } from "api/api-hooks/object-dataset-hooks";
import { ObjectDatasetDataDto } from "api/model/object-dataset/types";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { ObjectDatasetConfig } from "./ObjectDatasetConfig";

export default function ObjectDataset(props: ObjectDatasetConfig) {
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [objectData, setObjectData] = useState<ObjectDatasetDataDto | null>(null);
  const [content, setContent] = useState("");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const company = useCurrentCompanyContext();
  const { data, isLoading } = useGetObjectDataset(
    props.identifier ?? "",
    company?.companyReference ?? "",
    {
      enabled: !!company?.companyReference || !!props.identifier,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (data && data.data?.length > 0) {
      const sortData = data.data.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      setObjectData(sortData[0]);
    }
  }, [data]);

  useEffect(() => {
    if (objectData) {
      const foundFile = objectData.fileNames.find(
        file => props.initialFileName && file === props.initialFileName
      );
      setContent(foundFile ?? objectData.fileNames[0]);
    }
  }, [objectData, props.initialFileName]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      const iframeDoc = iframe.contentWindow.document;
      const height = Array.from(iframeDoc.body.children).reduce((acc, child) => {
        return acc + child.scrollHeight;
      }, 0);
      iframe.style.height = `${height + 40}px`;
    }
    setIsLoadingFile(false);
  };

  const fileUrl = encodeURI(
    `${window.contextRoot}/rest/object-dataset/${company?.companyReference}/${objectData?.identifier}/${objectData?.timestamp}/${content}`
  );

  return (
    <div className="flex flex-wrap gap-3">
      <div className="flex gap-2 w-full">
        <Dropdown
          options={data?.data ?? []}
          onChange={e => {
            setObjectData(e.value);
            setContent(e.value.fileNames[0]);
          }}
          optionLabel="timestamp"
          value={objectData}
          itemTemplate={option =>
            option && parseToLocalZoneDateTime(option.timestamp).format(DEFAULT_DATE_FORMATTER)
          }
          valueTemplate={
            objectData
              ? option => parseToLocalZoneDateTime(option.timestamp).format(DEFAULT_DATE_FORMATTER)
              : null
          }
          placeholder={T("select.model-version")}
          emptyMessage={T("message.no-models")}
          loading={isLoading}
        />
        {objectData && objectData.fileNames.length > 1 && data?.type !== "HTML" && (
          <Dropdown
            options={objectData.fileNames}
            value={content}
            onChange={e => setContent(e.value)}
          />
        )}
      </div>
      {isLoadingFile && (
        <div className="w-full align-self-center">
          <ProgressSpinner style={{ width: "10rem" }} />
        </div>
      )}
      {!content && (
        <div className="text-sm text-gray-500 align-self-center my-5">{T("message.no-models")}</div>
      )}
      {content && data?.type === "IMAGE" && (
        <img
          src={fileUrl}
          alt="model-view"
          loading="lazy"
          onLoad={() => setIsLoadingFile(false)}
          style={{ objectFit: "contain", width: "100%", height: "auto" }}
        />
      )}
      {content && data?.type !== "IMAGE" && (
        <iframe
          ref={iframeRef}
          loading="lazy"
          src={fileUrl}
          title="model-view"
          onLoad={handleIframeLoad}
          style={{ maxHeight: "200vh" }}
          className="flex-1 border-none"
        />
      )}
    </div>
  );
}
